import { useState, useEffect, useCallback, useRef } from 'react';
import { analytics as analyticsApi, sessions as sessionsApi, tags as tagsApi } from '../api/client';
import { buildProgressionSeries, computeSessionAverages } from '../utils/analyticsHelpers';
import StatCard from '../components/StatCard';
import MiniLineChart from '../components/MiniLineChart';
import MiniBarChart from '../components/MiniBarChart';
import HeatmapChart from '../components/HeatmapChart';
import HistogramChart from '../components/HistogramChart';
import TagFilterPills from '../components/TagFilterPills';
import useAIJournal from '../hooks/useAIJournal';

const TABS = ['Overview', 'Temporal', 'Distribution', 'AI Journal'];

const JOURNAL_PROMPTS = [
  { label: 'Full Report', text: 'Generate a comprehensive trading performance report based on my analytics data. Cover strengths, weaknesses, patterns, and give specific recommendations.' },
  { label: 'Weaknesses', text: 'Analyze my weaknesses as a trader based on my stats. What patterns show I am losing money? Be specific and data-driven.' },
  { label: 'Improvement Plan', text: 'Based on my trading data, create a prioritized improvement plan. What should I focus on first to improve my results?' },
  { label: 'Risk Analysis', text: 'Analyze my risk management. Look at my drawdown, streaks, risk/reward ratio, and holding times. Am I managing risk well?' },
];

function AIJournalTab({ analyticsData }) {
  const ai = useAIJournal(analyticsData);
  const [input, setInput] = useState('');
  const [showSettings, setShowSettings] = useState(!ai.isConfigured);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ai.messages, ai.streaming]);

  const handleSend = () => {
    if (!input.trim() || ai.streaming) return;
    ai.sendMessage(input);
    setInput('');
  };

  return (
    <div className="ai-journal-tab">
      <div className="ai-journal-header">
        <h3>AI Trading Journal</h3>
        <button className={`btn-icon ${showSettings ? 'active' : ''}`}
          onClick={() => setShowSettings(v => !v)} title="Settings">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.32 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        </button>
        {ai.messages.length > 0 && (
          <button className="btn-icon" onClick={ai.clearChat} title="Clear">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
          </button>
        )}
      </div>

      {showSettings && (
        <div className="ai-settings-form" style={{ marginBottom: 12 }}>
          <div className="ai-field">
            <label>Provider</label>
            <select value={ai.provider} onChange={e => ai.setProvider(e.target.value)}>
              <option value="claude">Claude (Anthropic)</option>
              <option value="openai">OpenAI</option>
              <option value="deepseek">DeepSeek</option>
              <option value="mistral">Mistral</option>
              <option value="grok">Grok (xAI)</option>
            </select>
          </div>
          <div className="ai-field">
            <label>Model</label>
            <input type="text" value={ai.model} onChange={e => ai.setModel(e.target.value)} />
          </div>
          <div className="ai-field">
            <label>API Key</label>
            <input type="password" value={ai.apiKey} onChange={e => ai.setApiKey(e.target.value)} placeholder="sk-..." />
          </div>
        </div>
      )}

      <div className="ai-journal-chat">
        {ai.messages.length === 0 && (
          <div className="ai-empty">
            <p>Let the AI analyze your trading performance and generate insights.</p>
            <div className="ai-quick-prompts">
              {JOURNAL_PROMPTS.map((qp, i) => (
                <button key={i} className="ai-quick-btn"
                  onClick={() => ai.sendMessage(qp.text)}
                  disabled={!ai.isConfigured || ai.streaming}>
                  {qp.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {ai.messages.map((msg, i) => (
          <div key={i} className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}>
            <div className="ai-message-label">{msg.role === 'user' ? 'You' : 'AI'}</div>
            <div className="ai-message-content">{msg.content || <span className="ai-typing">...</span>}</div>
          </div>
        ))}
        {ai.error && <div className="ai-error">{ai.error}</div>}
        <div ref={chatEndRef} />
      </div>

      <div className="ai-input-area">
        {ai.streaming ? (
          <button className="btn btn-sm btn-danger ai-stop-btn" onClick={ai.stopStreaming}>Stop</button>
        ) : (
          <>
            <textarea className="ai-input" rows={2} value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={ai.isConfigured ? 'Ask about your trading...' : 'Configure API key first'}
              disabled={!ai.isConfigured} />
            <button className="ai-send-btn" onClick={handleSend} disabled={!input.trim() || !ai.isConfigured}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [progression, setProgression] = useState(null);
  const [sessionAvgs, setSessionAvgs] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');

  const [instrument, setInstrument] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [challengeFilter, setChallengeFilter] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [allSessionsList, setAllSessionsList] = useState([]);
  const [sortKey, setSortKey] = useState('completed_at');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    tagsApi.list().then((res) => setAllTags(res.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (instrument) params.instrument = instrument;
    if (selectedTags.length > 0) params.tags = selectedTags.join(',');
    if (challengeFilter) params.challenge = challengeFilter;
    if (sessionFilter) params.session_id = sessionFilter;

    Promise.all([
      analyticsApi.summary(params),
      sessionsApi.list({ status: 'completed' }),
    ]).then(([summaryRes, sessionsRes]) => {
      setSummary(summaryRes.data);
      setAllSessionsList(sessionsRes.data);
      let completed = sessionsRes.data.filter(
        (s) => s.status === 'completed' && s.stats && s.stats.total_trades > 0
      );
      if (instrument) completed = completed.filter((s) => s.instrument === instrument);
      completed.sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));
      setSessions(completed);
      setProgression(buildProgressionSeries(completed));
      setSessionAvgs(computeSessionAverages(completed));
      const ins = [...new Set(sessionsRes.data.map((s) => s.instrument))].sort();
      setInstruments(ins);
    }).catch(console.error).finally(() => setLoading(false));
  }, [instrument, selectedTags, challengeFilter, sessionFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const overall = summary?.overall || {};

  const sortedSessions = [...sessions].sort((a, b) => {
    let va, vb;
    if (sortKey === 'completed_at') { va = new Date(a.completed_at); vb = new Date(b.completed_at); }
    else if (sortKey === 'name') { va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); }
    else { va = a.stats?.[sortKey] ?? 0; vb = b.stats?.[sortKey] ?? 0; }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortIcon = (key) => sortKey !== key ? '' : sortDir === 'asc' ? ' \u25B2' : ' \u25BC';

  if (loading) return <div className="page">Loading analytics...</div>;

  const hasData = overall.total_trades > 0;

  return (
    <div className="page analytics-page">
      <div className="page-header"><h1>Analytics</h1></div>

      {/* Filters */}
      <div className="analytics-filters">
        <div className="filter-group">
          <label>Instrument</label>
          <select value={instrument} onChange={(e) => setInstrument(e.target.value)}>
            <option value="">All</option>
            {instruments.map((ins) => <option key={ins} value={ins}>{ins}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Tags</label>
          <TagFilterPills tags={allTags} selectedTagIds={selectedTags} onChange={setSelectedTags} />
        </div>
        <div className="filter-group">
          <label>Session</label>
          <select value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)}>
            <option value="">All Sessions</option>
            {allSessionsList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || `Session #${s.id}`} — {s.instrument}
                {s.challenge_config ? ` (${s.challenge_config.name})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Type</label>
          <div className="filter-toggles">
            {[['', 'All'], ['yes', 'Challenge'], ['no', 'Free']].map(([v, label]) => (
              <button key={v}
                className={`filter-toggle ${challengeFilter === v ? 'active' : ''}`}
                onClick={() => setChallengeFilter(v)}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn-sm" onClick={() => { setInstrument(''); setSelectedTags([]); setChallengeFilter(''); setSessionFilter(''); }}>Clear</button>
      </div>

      {/* Tabs */}
      <div className="analytics-tabs">
        {TABS.map(tab => (
          <button key={tab}
            className={`analytics-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab}
          </button>
        ))}
      </div>

      {!hasData && activeTab !== 'AI Journal' ? (
        <div className="empty-state">No completed sessions with trades yet.</div>
      ) : (
        <>
          {/* ══════ TAB: Overview ══════ */}
          {activeTab === 'Overview' && (
            <>
              <div className="analytics-metrics">
                <StatCard label="Total Trades" value={overall.total_trades} format="count" />
                <StatCard label="Win Rate" value={overall.win_rate} format="percent" />
                <StatCard label="Total P&L" value={overall.total_pnl} format="pnl" colorize />
                <StatCard label="Profit Factor" value={overall.profit_factor} format="ratio" />
                <StatCard label="Expectancy" value={overall.expectancy} format="pnl" colorize />
                <StatCard label="Avg Win" value={overall.avg_win} format="pnl" colorize />
                <StatCard label="Avg Loss" value={-overall.avg_loss} format="pnl" colorize />
                <StatCard label="Risk/Reward" value={overall.risk_reward_ratio} format="ratio" />
                <StatCard label="Total Lots" value={overall.total_lots} format="ratio" />
                <StatCard label="Sharpe Ratio" value={overall.sharpe_ratio} format="ratio" />
                <StatCard label="Sortino Ratio" value={overall.sortino_ratio} format="ratio" />
                <StatCard label="Max Drawdown" value={overall.max_drawdown ? -overall.max_drawdown : 0} format="pnl" colorize />
                <StatCard label="Recovery Factor" value={overall.recovery_factor} format="ratio" />
                <StatCard label="Max Win Streak" value={overall.max_win_streak} format="count" />
                <StatCard label="Max Loss Streak" value={overall.max_loss_streak} format="count" />
                <StatCard label="Avg Holding" value={overall.avg_holding_seconds ? Math.round(overall.avg_holding_seconds / 60) + ' min' : '—'} format="default" />
                {sessionAvgs && <StatCard label="Sessions" value={sessionAvgs.session_count} format="count" />}
              </div>

              {/* Equity Curve */}
              {summary?.equity_curve?.length > 1 && (
                <>
                  <h2 className="analytics-section-title">Equity Curve</h2>
                  <div className="analytics-chart-card" style={{ marginBottom: '1.5rem' }}>
                    <MiniLineChart
                      data={summary.equity_curve.map((e, i) => ({
                        x: i, y: e.cumulative_pnl,
                        label: `Trade #${i + 1}`,
                        date: e.timestamp ? new Date(e.timestamp).toLocaleDateString() : '',
                      }))}
                      color="#6366f1" height={280}
                      yFormat={(v) => v.toFixed(2)}
                    />
                  </div>
                </>
              )}

              {/* Progression Charts */}
              {progression && sessions.length > 1 && (
                <>
                  <h2 className="analytics-section-title">Progression</h2>
                  <div className="analytics-charts">
                    <div className="analytics-chart-card">
                      <div className="analytics-chart-title">Win Rate per Session</div>
                      <MiniLineChart data={progression.winRate} color="#6366f1" height={200} yFormat={(v) => v.toFixed(1) + '%'} />
                    </div>
                    <div className="analytics-chart-card">
                      <div className="analytics-chart-title">Expectancy per Session</div>
                      <MiniLineChart data={progression.expectancy} color="#22c55e" height={200} yFormat={(v) => v.toFixed(2)} />
                    </div>
                    <div className="analytics-chart-card">
                      <div className="analytics-chart-title">Cumulative P&L</div>
                      <MiniLineChart data={progression.cumulativePnl} color="#f59e0b" height={200} yFormat={(v) => v.toFixed(2)} />
                    </div>
                  </div>
                </>
              )}

              {/* Session Table */}
              {sessions.length > 0 && (
                <>
                  <h2 className="analytics-section-title">Session Comparison</h2>
                  <div className="analytics-table-wrapper">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('name')}>Session{sortIcon('name')}</th>
                          <th>Instrument</th>
                          <th onClick={() => handleSort('completed_at')}>Date{sortIcon('completed_at')}</th>
                          <th onClick={() => handleSort('total_trades')}>Trades{sortIcon('total_trades')}</th>
                          <th onClick={() => handleSort('win_rate')}>Win Rate{sortIcon('win_rate')}</th>
                          <th onClick={() => handleSort('total_pnl')}>P&L{sortIcon('total_pnl')}</th>
                          <th onClick={() => handleSort('expectancy')}>Expectancy{sortIcon('expectancy')}</th>
                          <th onClick={() => handleSort('max_drawdown')}>Max DD{sortIcon('max_drawdown')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSessions.map((s) => {
                          const st = s.stats || {};
                          const pnl = st.total_pnl ?? 0;
                          return (
                            <tr key={s.id}>
                              <td>{s.name || `Session #${s.id}`}</td>
                              <td><span className="badge">{s.instrument}</span></td>
                              <td>{s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—'}</td>
                              <td>{st.total_trades ?? 0}</td>
                              <td>{st.win_rate != null ? st.win_rate.toFixed(1) + '%' : '—'}</td>
                              <td className={pnl > 0 ? 'pnl-positive' : pnl < 0 ? 'pnl-negative' : ''}>{pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}</td>
                              <td>{st.expectancy != null ? st.expectancy.toFixed(2) : '—'}</td>
                              <td>{st.max_drawdown != null ? st.max_drawdown.toFixed(2) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Best/Worst Day */}
              {(overall.best_day || overall.worst_day) && (
                <div className="analytics-segments" style={{ marginTop: '1.5rem' }}>
                  {overall.best_day && (
                    <div className="stat-card">
                      <div className="stat-label">Best Day</div>
                      <div className="stat-value pnl-positive">{overall.best_day.date}: +{overall.best_day.pnl}</div>
                    </div>
                  )}
                  {overall.worst_day && (
                    <div className="stat-card">
                      <div className="stat-label">Worst Day</div>
                      <div className="stat-value pnl-negative">{overall.worst_day.date}: {overall.worst_day.pnl}</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ══════ TAB: Temporal ══════ */}
          {activeTab === 'Temporal' && (
            <>
              {/* Heatmap */}
              {summary?.heatmap?.length > 0 && (
                <div className="analytics-chart-card" style={{ marginBottom: '1.5rem' }}>
                  <HeatmapChart data={summary.heatmap} title="P&L Heatmap — Day × Hour" />
                </div>
              )}

              <div className="analytics-charts">
                {/* By Day of Week */}
                {summary?.by_day_of_week?.length > 0 && (
                  <div className="analytics-chart-card">
                    <div className="analytics-chart-title">P&L by Day of Week</div>
                    <MiniBarChart
                      data={summary.by_day_of_week.map(d => ({ label: d.label, value: d.total_pnl }))}
                      height={220}
                    />
                  </div>
                )}

                {/* By Hour */}
                {summary?.by_hour?.length > 0 && (
                  <div className="analytics-chart-card">
                    <div className="analytics-chart-title">P&L by Hour</div>
                    <MiniBarChart
                      data={summary.by_hour.map(h => ({ label: h.hour + 'h', value: h.total_pnl }))}
                      height={220}
                    />
                  </div>
                )}

                {/* By Duration */}
                {summary?.by_duration?.length > 0 && (
                  <div className="analytics-chart-card">
                    <div className="analytics-chart-title">P&L by Trade Duration</div>
                    <MiniBarChart
                      data={summary.by_duration.map(d => ({ label: d.bucket, value: d.total_pnl }))}
                      height={220}
                    />
                  </div>
                )}
              </div>

              {/* Win rate tables */}
              {summary?.by_day_of_week?.length > 0 && (
                <div className="analytics-table-wrapper" style={{ marginTop: '1.5rem' }}>
                  <table className="analytics-table">
                    <thead>
                      <tr><th>Day</th><th>Trades</th><th>Win Rate</th><th>Total P&L</th><th>Avg P&L</th></tr>
                    </thead>
                    <tbody>
                      {summary.by_day_of_week.map((d, i) => (
                        <tr key={i}>
                          <td>{d.label}</td>
                          <td>{d.trade_count}</td>
                          <td>{d.win_rate}%</td>
                          <td className={d.total_pnl > 0 ? 'pnl-positive' : d.total_pnl < 0 ? 'pnl-negative' : ''}>{d.total_pnl.toFixed(2)}</td>
                          <td>{d.avg_pnl.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ══════ TAB: Distribution ══════ */}
          {activeTab === 'Distribution' && (
            <>
              {/* P&L Distribution */}
              {summary?.pnl_distribution?.length > 0 && (
                <div className="analytics-chart-card" style={{ marginBottom: '1.5rem' }}>
                  <HistogramChart data={summary.pnl_distribution} title="P&L Distribution" />
                </div>
              )}

              {/* Streaks */}
              {overall.max_win_streak > 0 && (
                <div className="analytics-metrics" style={{ marginBottom: '1.5rem' }}>
                  <StatCard label="Current Streak" value={overall.current_streak} format="count" colorize />
                  <StatCard label="Max Win Streak" value={overall.max_win_streak} format="count" />
                  <StatCard label="Max Loss Streak" value={overall.max_loss_streak} format="count" />
                  <StatCard label="Largest Win" value={overall.largest_win} format="pnl" colorize />
                  <StatCard label="Largest Loss" value={overall.largest_loss} format="pnl" colorize />
                  <StatCard label="Calmar Ratio" value={overall.calmar_ratio} format="ratio" />
                </div>
              )}

              {/* Breakdowns */}
              <div className="analytics-segments">
                {summary?.by_tag?.length > 0 && (
                  <div className="analytics-chart-card">
                    <div className="analytics-chart-title">P&L by Tag</div>
                    <MiniBarChart
                      data={summary.by_tag.map((t) => ({ label: t.tag_name, value: t.total_pnl, color: t.tag_color }))}
                      height={220}
                    />
                  </div>
                )}
                {summary?.by_instrument?.length > 0 && (
                  <div className="analytics-chart-card">
                    <div className="analytics-chart-title">P&L by Instrument</div>
                    <MiniBarChart
                      data={summary.by_instrument.map((i) => ({ label: i.instrument, value: i.total_pnl }))}
                      height={220}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══════ TAB: AI Journal ══════ */}
          {activeTab === 'AI Journal' && (
            <AIJournalTab analyticsData={summary} />
          )}
        </>
      )}
    </div>
  );
}
